import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import dayjs from 'dayjs'
import PropTypes from 'prop-types'
import React from 'react'

import {
  getPreschoolTeacherRemapEligibility,
  remapPreschoolStudents
} from 'api'
import ModalForm from 'app/components/ModalForm'
import constants from 'app/constants'
import { confirmDialog, eventAlert, eventEmitter } from 'app/helpers'

const useStyles = makeStyles((theme) => ({
  section: {
    marginBottom: theme.spacing(2)
  },
  sectionTitle: {
    fontSize: '13px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1)
  },
  controlsRow: {
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    alignItems: 'flex-start'
  },
  control: {
    minWidth: '240px'
  },
  preview: {
    backgroundColor: theme.palette.grey[100],
    borderRadius: '4px',
    padding: theme.spacing(2)
  },
  previewLine: {
    fontSize: '14px',
    marginBottom: theme.spacing(0.5)
  },
  createNotice: {
    color: theme.palette.warning.dark,
    fontWeight: 600
  },
  blocker: {
    color: theme.palette.error.main,
    fontWeight: 600
  },
  warning: {
    color: theme.palette.warning.dark,
    fontSize: '13px',
    marginTop: theme.spacing(0.5)
  },
  muted: {
    color: theme.palette.text.secondary,
    fontSize: '12px'
  },
  teacherControl: {
    minWidth: '380px',
    flex: 1
  },
  optionMeta: {
    fontSize: '12px',
    color: theme.palette.text.secondary
  },
  progress: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(5, 2),
    textAlign: 'center'
  },
  elapsed: {
    fontSize: '12px',
    color: theme.palette.text.secondary,
    fontVariantNumeric: 'tabular-nums'
  },
  rosterWrap: {
    maxHeight: '300px',
    overflowY: 'auto'
  },
  resultRow: {
    fontSize: '13px',
    marginBottom: theme.spacing(0.5)
  },
  failure: {
    color: theme.palette.error.main
  },
  batchOption: {
    alignItems: 'flex-start',
    marginBottom: theme.spacing(1),
    '& .MuiRadio-root': {
      paddingTop: theme.spacing(0.5)
    }
  },
  batchTag: {
    color: theme.palette.warning.dark,
    fontWeight: 600
  }
}))

const today = () => dayjs().format('YYYY-MM-DD')

// Every option is a preschool teacher, so the useful distinctions are which
// preschool programmes they run and where. Shown in full rather than truncated:
// a teacher runs at most five programmes, and eliding one hides exactly the
// fact that decides the move — whether they already run the programme the
// child is on.
const teacherMeta = (teacher) =>
  [(teacher.programmes || []).join(', '), (teacher.centres || []).join(', ')]
    .filter(Boolean)
    .join(' · ')

// "Mon 09:00AM-01:00PM", "Tue 09:00AM-01:00PM", … -> "Mon, Tue 09:00AM-01:00PM".
// Preschool batches run the same hours every weekday, so the long form is five
// copies of one fact.
const compactTimings = (timings = []) => {
  const daysByTime = {}
  timings.forEach((timing) => {
    const [day, ...rest] = timing.split(' ')
    const time = rest.join(' ')
    daysByTime[time] = [...(daysByTime[time] || []), day]
  })
  return Object.entries(daysByTime)
    .map(([time, days]) => `${days.join(', ')} ${time}`)
    .join(' · ')
}

// The "create a new batch" choice; mirrors NEW_BATCH in preschool_teacher_remap.py.
const NEW_BATCH = 'new'

const RemapStudentsModal = ({ isModalOpen, batch, onClose }) => {
  const classes = useStyles()
  const [loading, setLoading] = React.useState(true)
  const [submitting, setSubmitting] = React.useState(false)
  const [eligibility, setEligibility] = React.useState(null)
  const [selected, setSelected] = React.useState([])
  const [newTeacher, setNewTeacher] = React.useState('')
  // '' until the admin picks; the preview then falls back to the backend's
  // suggestion and echoes it as destination.target.
  const [targetBatch, setTargetBatch] = React.useState('')
  const [effectiveDate, setEffectiveDate] = React.useState(today())
  const [result, setResult] = React.useState(null)
  const [elapsed, setElapsed] = React.useState(0)

  // A remap rebuilds every forward-dated session, so this can run for a minute
  // or more. An indeterminate spinner alone reads as a hang at that length —
  // a climbing counter is what shows it is still working.
  React.useEffect(() => {
    if (!submitting) {
      setElapsed(0)
      return undefined
    }
    const startedAt = Date.now()
    const id = setInterval(
      () => setElapsed(Math.round((Date.now() - startedAt) / 1000)),
      1000
    )
    return () => clearInterval(id)
  }, [submitting])

  const batchUuid = batch?.uuid

  // Roster + teacher list. Fetched once per open; the destination preview is a
  // separate, cheaper refetch driven by the admin's picks.
  React.useEffect(() => {
    if (!isModalOpen || !batchUuid) return
    let cancelled = false
    ;(async () => {
      try {
        setLoading(true)
        const response = await getPreschoolTeacherRemapEligibility({
          batchUuid
        })
        if (cancelled) return
        setEligibility(response?.data)
      } catch (error) {
        if (cancelled) return
        eventAlert(
          error?.message || 'Unable to load this batch for remapping.',
          constants.ERROR
        )
        onClose()
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isModalOpen, batchUuid])

  // Reset between openings so a previous batch's selection can never leak into
  // the next one.
  React.useEffect(() => {
    if (isModalOpen) return
    setEligibility(null)
    setSelected([])
    setNewTeacher('')
    setTargetBatch('')
    setEffectiveDate(today())
    setResult(null)
  }, [isModalOpen])

  // Destination resolution. Re-run whenever a pick changes, so the numbers on
  // screen always describe the current selection rather than a stale one.
  React.useEffect(() => {
    if (!isModalOpen || !batchUuid || !newTeacher) return
    let cancelled = false
    ;(async () => {
      try {
        const response = await getPreschoolTeacherRemapEligibility({
          batchUuid,
          newTeacher,
          effectiveDate,
          programEnrolments: selected,
          targetBatch
        })
        if (!cancelled) setEligibility(response?.data)
      } catch (error) {
        if (!cancelled) {
          eventAlert(
            error?.message || 'Unable to resolve the destination batch.',
            constants.ERROR
          )
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isModalOpen, batchUuid, newTeacher, effectiveDate, selected, targetBatch])

  const students = eligibility?.students || []
  const teachers = eligibility?.teachers || []
  const teacherBatches = eligibility?.teacher_batches || []
  const destination = eligibility?.destination
  const chosenTarget = targetBatch || destination?.target || ''
  const warnings = eligibility?.warnings || []

  // A new teacher means a new batch list: drop the previous teacher's batches
  // and pick rather than flash them until the refetch lands.
  const changeTeacher = (value) => {
    setNewTeacher(value)
    setTargetBatch('')
    setEligibility((prev) =>
      prev
        ? {
            ...prev,
            teacher_batches: [],
            suggested_target: null,
            destination: null,
            warnings: []
          }
        : prev
    )
  }
  // Fallback mirrors MAX_STUDENTS_PER_REMAP in preschool_teacher_remap.py.
  const maxStudents = eligibility?.max_students_per_remap || 10

  const toggleStudent = (uuid) => {
    setSelected((prev) =>
      prev.includes(uuid) ? prev.filter((u) => u !== uuid) : [...prev, uuid]
    )
  }

  const allSelected = students.length > 0 && selected.length === students.length
  const toggleAll = () => {
    setSelected(allSelected ? [] : students.map((s) => s.uuid))
  }

  const selectedSessions = students
    .filter((s) => selected.includes(s.uuid))
    .reduce((total, s) => total + (s.future_sessions || 0), 0)

  const teacherName = React.useMemo(
    () =>
      teachers.find((t) => t.uuid === newTeacher)?.name || 'the new teacher',
    [teachers, newTeacher]
  )
  const currentTeacherName =
    eligibility?.batch?.teacher_name ||
    batch?.teacher_name ||
    'the current teacher'
  // Preschool batches carry no `name` on production — the API composes a
  // course/level/teacher label instead.
  const batchLabel =
    eligibility?.batch?.label ||
    batch?.name ||
    [batch?.course_name, batch?.level].filter(Boolean).join(' · ') ||
    ''

  const handleSave = async () => {
    if (!selected.length) {
      eventAlert('Select at least one student to remap.', constants.ERROR)
      eventEmitter.emit(constants.MODAL_FORM_SAVE_ERROR)
      return
    }
    if (!newTeacher) {
      eventAlert(
        'Pick the teacher these students are moving to.',
        constants.ERROR
      )
      eventEmitter.emit(constants.MODAL_FORM_SAVE_ERROR)
      return
    }
    if (selected.length > maxStudents) {
      eventAlert(
        `Remap up to ${maxStudents} students at a time. Split this into ` +
          'smaller passes.',
        constants.ERROR
      )
      eventEmitter.emit(constants.MODAL_FORM_SAVE_ERROR)
      return
    }
    if (!chosenTarget) {
      eventAlert(
        'Pick the batch these students are moving into.',
        constants.ERROR
      )
      eventEmitter.emit(constants.MODAL_FORM_SAVE_ERROR)
      return
    }
    if (destination?.blocker) {
      eventAlert(destination.blocker, constants.ERROR)
      eventEmitter.emit(constants.MODAL_FORM_SAVE_ERROR)
      return
    }

    // Creating a batch is a side effect the admin should agree to by name, not
    // discover afterwards — so it gets its own line in the confirmation.
    const createLine =
      chosenTarget === NEW_BATCH
        ? `\n\nA NEW batch will be created for ${teacherName}, cloned from ` +
          `"${batchLabel}".`
        : `\n\nInto: ${destination?.batch?.course_name || 'the chosen batch'}` +
          ` (${destination?.batch?.centre_name || ''}).`
    // Repeated here, not only in the preview: a cross-programme move with no
    // fee change is the one outcome that has to be agreed to, not skimmed.
    const warningLines = warnings.map((w) => `\n\n⚠ ${w}`).join('')
    const { isConfirmed } = await confirmDialog({
      text:
        `Move ${selected.length} student(s) from ${currentTeacherName} to ` +
        `${teacherName}, effective ${dayjs(effectiveDate).format(
          'DD MMM YYYY'
        )}?` +
        `\n\n${selectedSessions} future session(s) will move. Sessions before ` +
        `that date stay with ${currentTeacherName}.` +
        createLine +
        warningLines,
      confirmButtonText: 'Remap students',
      options: { title: 'Confirm remap' }
    })
    if (!isConfirmed) {
      eventEmitter.emit(constants.MODAL_FORM_SAVE_ERROR)
      return
    }

    try {
      setSubmitting(true)
      const response = await remapPreschoolStudents({
        batchUuid,
        newTeacher,
        programEnrolments: selected,
        effectiveDate,
        targetBatch: chosenTarget
      })
      setResult(response?.data)
      eventEmitter.emit(constants.CLOSE_MODAL_FORM)
    } catch (error) {
      eventAlert(error?.message || 'Remap failed', constants.ERROR)
      eventEmitter.emit(constants.MODAL_FORM_SAVE_ERROR)
    } finally {
      setSubmitting(false)
    }
  }

  // --- in-progress view. ModalForm's `loading` prop only disables its button
  // (and its "Saving" label is short-circuited by our own buttonLabel), so on
  // its own it looks like the click did nothing. This replaces the form for the
  // duration and says what is happening.
  if (submitting) {
    return (
      <ModalForm
        isModalOpen={isModalOpen}
        modalTitle="Remapping…"
        enableSave={false}
        maxWidth="md"
        disableBackdropClick
        onModalClose={() => {}}
      >
        <Box className={classes.progress}>
          <CircularProgress />
          <Typography className={classes.previewLine}>
            Moving <strong>{selected.length}</strong> student(s) to{' '}
            <strong>{teacherName}</strong>
          </Typography>
          <Typography className={classes.muted}>
            Rebuilding {selectedSessions} session(s). This usually takes under a
            minute — please keep this window open.
          </Typography>
          <Typography className={classes.elapsed}>
            {elapsed}s elapsed
          </Typography>
        </Box>
      </ModalForm>
    )
  }

  // --- result view: shown in place of the form once a remap has run. Stays on
  // screen (rather than a toast) because a partial success needs reading.
  if (result) {
    const failures = (result.results || []).filter((r) => !r.success)
    return (
      <ModalForm
        isModalOpen={isModalOpen}
        onModalClose={onClose}
        modalTitle="Remap complete"
        enableSave={false}
        maxWidth="md"
      >
        <Box className={classes.section}>
          <Typography className={classes.previewLine}>
            <strong>{result.moved}</strong> student(s) moved to{' '}
            <strong>{result.target_batch?.teacher_name}</strong> —{' '}
            {result.target_batch?.label}
            {result.created_batch ? ' (newly created)' : ''}.
          </Typography>
          {result.failed > 0 && (
            <Typography className={`${classes.previewLine} ${classes.failure}`}>
              <strong>{result.failed}</strong> failed — these students are still
              on the original batch.
            </Typography>
          )}
        </Box>
        <Box className={classes.section}>
          {(result.results || []).map((row) => (
            <div
              key={row.uuid}
              className={`${classes.resultRow} ${
                row.success ? '' : classes.failure
              }`}
            >
              {row.success ? '✓' : '✕'} {row.student_name}
              {row.success
                ? ` — ${row.sessions_dropped} session(s) moved, chat ${
                    row.chat
                  }${row.grade ? `, grade ${row.grade}` : ''}`
                : ` — ${row.error}`}
            </div>
          ))}
        </Box>
        {failures.length > 0 && (
          <Typography className={classes.muted}>
            Failed rows can be retried — reopen Remap student and select just
            those children.
          </Typography>
        )}
      </ModalForm>
    )
  }

  return (
    <ModalForm
      isModalOpen={isModalOpen}
      onModalClose={onClose}
      onSaveClick={handleSave}
      modalTitle={`Remap student — ${batchLabel}`}
      modalSubtitle={`Currently taught by ${currentTeacherName}`}
      buttonLabel="Review and remap"
      maxWidth="md"
      loading={loading || submitting ? true : undefined}
    >
      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box className={classes.section}>
            <div className={classes.sectionTitle}>
              1 · Students on this batch
            </div>
            {students.length === 0 ? (
              <Typography className={classes.muted}>
                No live students on this batch.
              </Typography>
            ) : (
              <TableContainer className={classes.rosterWrap}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={allSelected}
                          indeterminate={selected.length > 0 && !allSelected}
                          onChange={toggleAll}
                        />
                      </TableCell>
                      <TableCell>Child</TableCell>
                      <TableCell>Parent</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell align="right">Future sessions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {students.map((student) => (
                      <TableRow
                        key={student.uuid}
                        hover
                        onClick={() => toggleStudent(student.uuid)}
                        style={{ cursor: 'pointer' }}
                      >
                        {/* The row is clickable too, so the checkbox cell has
                            to swallow its click: without this the Checkbox's
                            onChange AND the row's onClick both fire, toggling
                            twice and leaving the box exactly as it was. */}
                        <TableCell
                          padding="checkbox"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox
                            checked={selected.includes(student.uuid)}
                            onChange={() => toggleStudent(student.uuid)}
                            inputProps={{
                              'aria-label': `Select ${student.student_name}`
                            }}
                          />
                        </TableCell>
                        <TableCell>{student.student_name}</TableCell>
                        <TableCell>{student.parent_name}</TableCell>
                        <TableCell>{student.parent_phone}</TableCell>
                        <TableCell align="right">
                          {student.future_sessions}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {selected.length > maxStudents && (
              <div className={classes.blocker}>
                {selected.length} selected — remap at most {maxStudents} at a
                time.
              </div>
            )}
          </Box>

          <Box className={classes.section}>
            <div className={classes.sectionTitle}>2 · Move them to</div>
            <div className={classes.controlsRow}>
              <FormControl className={classes.teacherControl}>
                <Select
                  value={newTeacher}
                  displayEmpty
                  onChange={(e) => changeTeacher(e.target.value)}
                  renderValue={(value) => {
                    if (!value) return <em>Select a teacher</em>
                    const picked = teachers.find((t) => t.uuid === value)
                    return picked
                      ? `${picked.name} — ${teacherMeta(picked)}`
                      : ''
                  }}
                >
                  <MenuItem value="">
                    <em>Select a teacher</em>
                  </MenuItem>
                  {teachers.map((teacher) => (
                    <MenuItem key={teacher.uuid} value={teacher.uuid}>
                      <Box>
                        <div>{teacher.name}</div>
                        <div className={classes.optionMeta}>
                          {teacherMeta(teacher)}
                        </div>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                className={classes.control}
                label="Effective from"
                type="date"
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: today() }}
                helperText={`Sessions before this date stay with ${currentTeacherName}`}
              />
            </div>
          </Box>

          {/* suggested_target is only set once the teacher's batches were
              computed — not for "that is already this batch's teacher". */}
          {newTeacher && eligibility?.suggested_target && (
            <Box className={classes.section}>
              <div className={classes.sectionTitle}>
                3 · Into which of {teacherName}&apos;s batches
              </div>
              <RadioGroup
                value={chosenTarget}
                onChange={(e) => setTargetBatch(e.target.value)}
              >
                {teacherBatches.map((option) => (
                  <FormControlLabel
                    key={option.uuid}
                    value={option.uuid}
                    control={<Radio size="small" />}
                    className={classes.batchOption}
                    label={
                      <Box>
                        <div>
                          {option.course_name || option.label}
                          {!option.same_programme && (
                            <span className={classes.batchTag}>
                              {' '}
                              · different programme
                            </span>
                          )}
                          {!option.same_centre && (
                            <span className={classes.batchTag}>
                              {' '}
                              · {option.centre_name}
                            </span>
                          )}
                        </div>
                        <div className={classes.optionMeta}>
                          {[
                            compactTimings(option.timings),
                            option.vacancy !== null &&
                            option.vacancy !== undefined
                              ? `${option.vacancy} of ${option.capacity} seats free`
                              : null,
                            option.status !== 'active' ? option.status : null
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </div>
                      </Box>
                    }
                  />
                ))}
                <FormControlLabel
                  value={NEW_BATCH}
                  control={<Radio size="small" />}
                  className={classes.batchOption}
                  label={
                    <Box>
                      <div>Create a new batch</div>
                      <div className={classes.optionMeta}>
                        Cloned from &quot;{batchLabel}&quot; — same cadence,
                        room, capacity and fee
                      </div>
                    </Box>
                  }
                />
              </RadioGroup>
              {teacherBatches.length === 0 && (
                <Typography className={classes.muted}>
                  {teacherName} has no other current preschool batch.
                </Typography>
              )}
            </Box>
          )}

          {destination && (
            <Box className={`${classes.section} ${classes.preview}`}>
              <div className={classes.sectionTitle}>4 · What will happen</div>
              {destination.blocker ? (
                <div className={classes.blocker}>{destination.blocker}</div>
              ) : (
                <>
                  {destination.mode === 'existing' ? (
                    <div className={classes.previewLine}>
                      Moving into existing batch{' '}
                      <strong>{destination.batch?.label}</strong> ({teacherName}
                      ) — {destination.batch?.vacancy} seat(s) free of{' '}
                      {destination.batch?.capacity}.
                    </div>
                  ) : (
                    <div
                      className={`${classes.previewLine} ${classes.createNotice}`}
                    >
                      A NEW batch will be created for {teacherName}, cloned from
                      &quot;{destination.clone_of?.label}&quot; (same cadence,
                      room, capacity and fee).
                    </div>
                  )}
                  <div className={classes.previewLine}>
                    <strong>{selected.length}</strong> student(s) selected ·{' '}
                    <strong>{selectedSessions}</strong> future session(s) will
                    be dropped from this batch and recreated on the destination.
                  </div>
                  <div className={classes.muted}>
                    Preschool schedules run to the end of the academic year, so
                    each child carries a few hundred forward sessions — this can
                    take up to a minute. Leave the tab open.
                  </div>
                  <div className={classes.previewLine}>
                    Parent–teacher chat channels will be re-pointed to{' '}
                    {teacherName} (created if one is missing). No messages are
                    sent to parents or teachers.
                  </div>
                  {warnings.map((warning) => (
                    <div key={warning} className={classes.warning}>
                      ⚠ {warning}
                    </div>
                  ))}
                </>
              )}
            </Box>
          )}
        </>
      )}
    </ModalForm>
  )
}

RemapStudentsModal.propTypes = {
  isModalOpen: PropTypes.bool,
  batch: PropTypes.object,
  onClose: PropTypes.func.isRequired
}

export default RemapStudentsModal
